import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import toast from "react-hot-toast";
import API from "../../api";
import { LinearProgressBar } from "../Loading";
import RateTile from "./RateTile";
import PeriodFilter from "./PeriodFilter";

const BLUE = { dark: "#0e48d8", base: "#165DFB", mid: "#6694ff", soft: "#95b8ff", pale: "#c0d5ff" };
const NEUTRAL = { slate: "#94a3b8", track: "#cbd5e1", line: "#e2e8f0" };
const STATUS = { attention: "#f97316", critical: "#dc2626" };
const AREA_RAMP = [BLUE.base, BLUE.mid, BLUE.soft, BLUE.pale, NEUTRAL.slate, NEUTRAL.track];

const TOOLTIP_STYLE = { backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.1)", fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: "#94a3b8" };

const fixed = (value) => (Number.isFinite(value) ? value.toFixed(1) : "—");
const sumOf = (rows, key) => rows.reduce((sum, row) => sum + (row[key] || 0), 0);

const percentageTooltipFormatter = (value, name, props) => {
  if (!props || !props.payload) return [value, name];
  const total = Object.keys(props.payload)
    .filter((key) => key !== "date" && typeof props.payload[key] === "number")
    .reduce((sum, key) => sum + props.payload[key], 0);
  return total ? [`${value} (${((value / total) * 100).toFixed(1)}%)`, name] : [`${value} (0.0%)`, name];
};

const MEDECIN_DEFS = [
  { key: "FOUND", label: "Trouvé", color: BLUE.base },
  { key: "TOO_MANY_RESULTS", label: "Trop de résultats", color: BLUE.soft },
  { key: "NOT_FOUND", label: "Non trouvé", color: NEUTRAL.track },
  { key: "NO_DOCTOR", label: "Pas de médecin", color: STATUS.attention },
];
const ALERT_DEFS = [
  { key: "Aucune alerte", label: "Aucune alerte", color: BLUE.base },
  { key: "Informations complémentaires", label: "Infos complémentaires", color: BLUE.soft },
  { key: "Texte manuscrit", label: "Texte manuscrit", color: NEUTRAL.slate },
  { key: "Conditions non traitées", label: "Conditions non traitées", color: STATUS.attention },
  { key: "Examens sans codes", label: "Examens sans codes", color: STATUS.critical },
];
const CODES_DEFS = [
  { key: "Aucun changement", label: "Aucun changement", color: BLUE.base },
  { key: "Codes ajoutés", label: "Codes ajoutés", color: BLUE.soft },
  { key: "Codes supprimés", label: "Codes supprimés", color: NEUTRAL.slate },
  { key: "Codes modifiés", label: "Codes modifiés", color: NEUTRAL.track },
];
const MISSING_DEFS = [
  { key: "Aucun codes ajoutés", label: "Aucun ajout", color: BLUE.base },
  { key: "Codes ajoutés avec alertes", label: "Ajoutés avec alertes", color: BLUE.soft },
  { key: "Codes ajoutés sans alertes", label: "Ajoutés sans alertes", color: STATUS.attention },
];

const rateData = (rows, goodKey, badKey) =>
  rows.map((row) => {
    const total = (row[goodKey] || 0) + (row[badKey] || 0);
    return { date: row.date, value: total ? Math.round(((row[goodKey] || 0) / total) * 100) : 0 };
  });

const rateHeadline = (rows, goodKey, badKey) => {
  const good = sumOf(rows, goodKey);
  const total = good + sumOf(rows, badKey);
  return total ? (good / total) * 100 : 0;
};

const ExtractionDashboard = () => {
  const [range, setRange] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["extraction-stats", range],
    enabled: !!range,
    queryFn: async () => {
      const res = await API.get(`/stats/extraction?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`);
      if (!res.ok) throw new Error(res.message);
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (error) toast.error(error.message || "Erreur lors du chargement des statistiques");
  }, [error]);

  const performance = data?.performance || [];
  const autonomyRate = data?.autonomyRate || [];
  const hourlyVolume = data?.hourlyVolume || [];
  const dailyCount = data?.dailyCount || [];
  const doctorStatus = data?.doctorStatus || [];
  const alerts = data?.alerts || [];
  const missingCodesDetection = data?.missingCodesDetection || [];
  const dateInsertion = data?.dateInsertion || [];
  const codesInsertion = data?.codesInsertion || [];

  const dailyTotalData = dailyCount.map((day) => ({ date: day.date, value: Object.entries(day).reduce((sum, [key, value]) => (key === "date" || typeof value !== "number" ? sum : sum + value), 0) }));
  const totalOrdonnances = dailyTotalData.reduce((sum, day) => sum + day.value, 0);
  const totalTimeData = performance.map((day) => ({ date: day.date, value: day.ocr + day.ner + day.other }));
  const avgTotalTime = performance.length ? performance.reduce((sum, day) => sum + day.ocr + day.ner + day.other, 0) / performance.length : 0;
  const avgAutonomy = autonomyRate.length ? autonomyRate.reduce((sum, day) => sum + day.rate, 0) / autonomyRate.length : 0;
  const indexKeys = dailyCount.length ? Object.keys(dailyCount[0]).filter((key) => key !== "date") : [];

  const kpiTiles = [
    { label: "Total ordonnances", subtitle: "Total sur la période", value: totalOrdonnances, data: dailyTotalData, color: BLUE.base, format: (value) => Math.round(value).toLocaleString("fr-FR") },
    { label: "Temps moyen", subtitle: "Moyenne par jour", value: avgTotalTime, data: totalTimeData, color: BLUE.mid, format: (value) => `${fixed(value)} s` },
  ];

  const rateTiles = [
    { label: "Taux d'autonomie", subtitle: "Traitées sans intervention", value: avgAutonomy, data: autonomyRate.map((day) => ({ date: day.date, value: day.rate })) },
    { label: "Insertion des dates", subtitle: "Date extraite et insérée", value: rateHeadline(dateInsertion, "Date insérée", "Date non insérée"), data: rateData(dateInsertion, "Date insérée", "Date non insérée") },
  ];

  const compositionCharts = [
    { title: "Codes médecins", subtitle: "Statut d'insertion", data: doctorStatus, defs: MEDECIN_DEFS },
    { title: "Alertes", subtitle: "Répartition des types", data: alerts, defs: ALERT_DEFS },
    { title: "Codes insérés", subtitle: "Modifications de codes", data: codesInsertion, defs: CODES_DEFS },
    { title: "Détection codes manquants", subtitle: "Efficacité de la détection", data: missingCodesDetection, defs: MISSING_DEFS },
  ];

  return (
    <div>
      <LinearProgressBar isVisible={isLoading} />

      <PeriodFilter onChange={setRange} />

      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {kpiTiles.map((tile) => (
            <RateTile key={tile.label} label={tile.label} subtitle={tile.subtitle} value={tile.value} data={tile.data} color={tile.color} format={tile.format} />
          ))}
        </div>

        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Performance</h3>
          <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">Temps de traitement</h2>
              <p className="mt-0.5 text-xs text-gray-500">OCR, IA et autre — moyenne par jour (secondes)</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={performance} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={24} />
                <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} width={44} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "#e5e7eb" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Area type="monotone" dataKey="ocr" stroke={BLUE.dark} fill={BLUE.dark} fillOpacity={0.16} strokeWidth={2} name="OCR" stackId="1" />
                <Area type="monotone" dataKey="ner" stroke={BLUE.mid} fill={BLUE.mid} fillOpacity={0.16} strokeWidth={2} name="IA" stackId="1" />
                <Area type="monotone" dataKey="other" stroke={NEUTRAL.slate} fill={NEUTRAL.slate} fillOpacity={0.16} strokeWidth={2} name="Autre" stackId="1" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Qualité d'extraction</h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {rateTiles.map((tile) => (
              <RateTile key={tile.label} label={tile.label} subtitle={tile.subtitle} value={tile.value} data={tile.data} color={BLUE.base} />
            ))}
            {compositionCharts.map((chart) => (
              <div key={chart.title} className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-gray-900">{chart.title}</h2>
                  <p className="mt-0.5 text-xs text-gray-500">{chart.subtitle}</p>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={chart.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} stackOffset="expand">
                    <CartesianGrid vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={24} />
                    <YAxis tickFormatter={(value) => `${Math.round(value * 100)}%`} tickLine={false} axisLine={false} tick={AXIS_TICK} width={44} />
                    <Tooltip formatter={percentageTooltipFormatter} contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "#e5e7eb" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    {chart.defs.map((def) => (
                      <Area key={def.key} type="monotone" dataKey={def.key} name={def.label} stroke={def.color} fill={def.color} fillOpacity={0.16} strokeWidth={2} stackId="1" />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Activité</h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-gray-900">Volume horaire</h2>
                <p className="mt-0.5 text-xs text-gray-500">Répartition du traitement par heure</p>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={hourlyVolume} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="hour" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={8} />
                  <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} width={44} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="count" fill={BLUE.base} name="Ordonnances" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-gray-900">Ordonnances par jour</h2>
                <p className="mt-0.5 text-xs text-gray-500">Répartition par index</p>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyCount} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={24} />
                  <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} width={44} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "#e5e7eb" }} />
                  {indexKeys.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />}
                  {indexKeys.map((key, idx) => (
                    <Area key={key} type="monotone" dataKey={key} stroke={AREA_RAMP[idx % AREA_RAMP.length]} fill={AREA_RAMP[idx % AREA_RAMP.length]} fillOpacity={0.12} strokeWidth={2} name={key} stackId="1" />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ExtractionDashboard;
